import { BroadcastActions, EventActions, UserState } from './types';

import type { AgendaFavorite, Broadcast } from '../event/types';

import loggerService from '@/services/logger.service';
import { UserRole } from '@/services/user.service';
import { RootState } from '@/store';

const getters = {
  getCurrentUser: ({ currentUser, currentEventUser }: UserState) => currentEventUser ?? currentUser,
  getCurrentUserId: (state: UserState) => {
    return state.currentUser?.id ?? null;
  },
  userAuthToken: (state: UserState) => state.accessToken,
  getPrivateDiscussionRoom: (state: UserState) => {
    return state.privateDiscussionRoom;
  },
  viewAsAttendee(state: UserState, getters: any, rootState: RootState, rootGetters: any) {
    if (rootState.event.stage?.isTVMode) {
      return true;
    }
    return state.currentEventUser?.eventUser?.view_as_attendee ?? false;
  },
  isOrganizer(state: UserState, getters: any) {
    return (state.currentEventUser?.status?.isOrganizer || getters.isProducer || getters.isCoordinator) ?? false;
  },
  // Adding this getter, in future we can use this and deprecate isOrganizer
  isOrganizerV2(state: UserState, getters: any): boolean {
    return (getters.isProducer || getters.isCoordinator) ?? false;
  },
  isStaffMember(state: UserState) {
    return state.currentEventUser?.event_role === UserRole.STAFF;
  },
  isProducer(state: UserState, getters: any) {
    return getters.staffRole === 'Producer';
  },
  isCoordinator(state: UserState, getters: any) {
    return getters.staffRole === 'Coordinator';
  },
  isEventOrganizer(state: UserState, g: any) {
    const getters = g as UserGetters;
    return getters.isOrganizer && !getters.viewAsAttendee;
  },
  // Adding this getter, in future we can use this and deprecate isEventOrganizer
  isEventOrganizerV2(state: UserState, g: any): boolean {
    const getters = g as UserGetters;
    return getters.isOrganizerV2 && !getters.viewAsAttendee;
  },
  agendaFavorites(state: UserState, getters: any, rootState: RootState): AgendaFavorite[] {
    const agendaFavorites = state.currentEventUser?.status?.agendaFavorites || [];

    if (rootState.event.event.on_demand_mode && agendaFavorites.length) {
      return agendaFavorites.filter(agendaFavorite => {
        if (agendaFavorite.agenda_item) {
          return false;
        }

        if (agendaFavorite.broadcast) {
          const broadcast = rootState.event.event.agenda?.broadcasts?.find(
            (b: Broadcast) => b.id === agendaFavorite.broadcast
          );

          if (!broadcast?.is_available_on_demand) {
            return false;
          }

          return true;
        }

        return true;
      });
    }

    return agendaFavorites;
  },
  isBroadcastActionPermitted:
    (state: UserState, getters: any, rootState: RootState, rootGetters: any) =>
    (action: BroadcastActions, broadcastId: string) => {
      const permissions = state.permissions?.broadcasts?.[broadcastId] ?? [];
      // If the action is 'go_backstage' and permissions are not empty, it means the user has permission to enter the backstage.
      // The 'go_backstage' permission is only available to users who have broadcast-level permissions.
      if (action === 'go_backstage' && permissions.length > 0) {
        return true;
      }
      const permitted = permissions.includes(action);
      if (!permitted) {
        logPermissionDenied(action, state.currentEventUser?.event, broadcastId);
      }
      return permitted;
    },
  isEventActionPermitted:
    (state: UserState, getters: any, rootState: RootState, rootGetters: any) => (action: EventActions) => {
      const eventId = state.currentEventUser?.event || '';
      const eventPermissions = state.permissions?.events?.[eventId];
      const permitted = eventPermissions?.includes(action);
      if (eventPermissions && !permitted) {
        logPermissionDenied(action, eventId);
      }
      return permitted;
    },
  staffRole(state: UserState, getters: any, rootState: RootState, rootGetters: any) {
    const isWebinar = rootGetters['event/isWebinar'];
    const currentSession = isWebinar
      ? rootGetters['event/currentEventAgenda']?.[0]
      : rootGetters['event/getCurrentRouteAgendaItem'];
    const eventId = rootState.route.params.eventID as string;
    const permissions = state.permissions;
    const isProducer = (permissions?.events?.[eventId]?.length || 0) > 0;
    const isCoordinator = (permissions?.broadcasts?.[currentSession?.id]?.length || 0) > 0;
    const isEventRoleEnabled = state.currentEventUser?.event_role === UserRole.STAFF;

    if (!isEventRoleEnabled) {
      return;
    }

    if (isProducer) {
      return 'Producer';
    }

    if (isCoordinator) {
      return 'Coordinator';
    }
  }
};

const permissionDiagnosticTimes = new Map<string, number>();
const PERMISSION_DIAGNOSTIC_COOLDOWN_MS = 30000;

function logPermissionDenied(action: string, eventId?: string, broadcastId?: string) {
  const key = `${action}:${eventId || ''}:${broadcastId || ''}`;
  const now = Date.now();
  if ((permissionDiagnosticTimes.get(key) ?? 0) + PERMISSION_DIAGNOSTIC_COOLDOWN_MS > now) {
    return;
  }
  permissionDiagnosticTimes.set(key, now);
  try {
    loggerService.info('app_action', {
      action: 'permission_denied',
      permission_action: action,
      event_id: eventId || '',
      ...(broadcastId ? { broadcast_id: broadcastId } : {})
    });
  } catch {
    // Permission checks must remain side-effect safe when diagnostics fail.
  }
}

export default getters;

type GetterTree = typeof getters;
type GetterFunc<k extends keyof GetterTree> = GetterTree[k];
export type UserGetters = {
  [k in keyof GetterTree]: ReturnType<GetterFunc<k>>;
};
